import { supabase } from '@/integrations/supabase/client';

export interface MentorData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  profession: string;
  location: string;
  avatar: string;
  bio: string;
  specialties: string[];
  rating?: number;
  reviews?: number;
}

export interface PublicMentorData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  profession: string;
  location: string;
  avatar: string;
  bio: string;
  specialties: string[];
  rating?: number;
  reviews?: number;
}

export interface SearchParams {
  location?: string;
  searchTerm?: string;
  limit?: number;
  offset?: number;
}

export async function fetchMentors(params: SearchParams = {}) {
  try {
    const { location, searchTerm, limit = 10, offset = 0 } = params;
    
    // Safe fields for public access - excluding sensitive data
    const safeFields = 'id, first_name, last_name, profession, location, avatar_url, bio, specialties, status, is_approved';
    
    let query = supabase
      .from('profiles')
      .select(safeFields)
      .eq('profile_type', 'mentor')
      .eq('status', 'Active')
      .eq('is_approved', true);

    // Apply location filter if provided
    if (location && location !== 'All Locations') {
      query = query.eq('location', location);
    }

    // Apply search term filter if provided
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.trim().toLowerCase();
      query = query.or(
        `first_name.ilike.%${term}%,last_name.ilike.%${term}%,profession.ilike.%${term}%,bio.ilike.%${term}%`
      );
    }
      
    // Apply pagination
    query = query.range(offset, offset + limit - 1);
    
    const { data, error } = await query;
      
    if (error) {
      console.error("Error fetching mentors:", error);
      return { success: false, error, data: [] };
    }
    
    if (!data || data.length === 0) {
      return { success: true, data: [] };
    }

    // Fetch reviews from both tables for all mentors
    const mentorIds = data.map(mentor => mentor.id);
    
    // Fetch appointment reviews (Voice/Video Call reviews)
    const { data: appointmentReviews } = await supabase
      .from('appointment_reviews')
      .select('reviewee_id, rating')
      .in('reviewee_id', mentorIds);

    // Fetch subscription reviews (Subscription reviews - mentee_to_mentor only)
    const { data: mentorReviews } = await supabase
      .from('subscription_reviews')
      .select('reviewee_id, rating')
      .in('reviewee_id', mentorIds)
      .eq('review_type', 'mentee_to_mentor');

    // Combine and group all reviews by mentor ID
    const allReviews = [...(appointmentReviews || []), ...(mentorReviews || [])];
    const reviewsByMentor = allReviews.reduce((acc: any, review: any) => {
      if (!acc[review.reviewee_id]) {
        acc[review.reviewee_id] = [];
      }
      acc[review.reviewee_id].push(review);
      return acc;
    }, {}) || {};
    
    // Transform the data to match our MentorData interface
    const transformedData: MentorData[] = data.map(mentor => {
      // Parse specialties - handle both string and array formats
      let specialtiesArray: string[] = ['Mentoring'];
      
      if (mentor.specialties) {
        try {
          if (typeof mentor.specialties === 'string') {
            const parsed = JSON.parse(mentor.specialties);
            if (Array.isArray(parsed)) {
              specialtiesArray = parsed;
            } else if (typeof parsed === 'string') {
              specialtiesArray = [parsed];
            }
          } else if (Array.isArray(mentor.specialties)) {
            specialtiesArray = mentor.specialties;
          }
        } catch (e) {
          console.error("Error parsing specialties:", e);
        }
      }
      
      // Create full name from first and last name
      const firstName = mentor.first_name || '';
      const lastName = mentor.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim() || 'Anonymous Mentor';
      
      // Calculate actual rating and review count from both tables
      const mentorReviews = reviewsByMentor[mentor.id] || [];
      const reviewCount = mentorReviews.length;
      const averageRating = reviewCount > 0 
        ? mentorReviews.reduce((sum: number, review: any) => sum + review.rating, 0) / reviewCount
        : undefined;
      
      return {
        id: mentor.id,
        name: fullName,
        firstName,
        lastName,
        profession: mentor.profession || 'Professional Mentor',
        location: mentor.location || 'Remote',
        avatar: mentor.avatar_url || '',
        bio: mentor.bio || 'Professional mentor ready to help you succeed.',
        specialties: specialtiesArray,
        rating: averageRating ? Math.round(averageRating * 10) / 10 : undefined, // Round to 1 decimal
        reviews: reviewCount
      };
    });
    
    return { success: true, data: transformedData };
  } catch (error) {
    console.error("Exception in fetchMentors:", error);
    return { success: false, error, data: [] };
  }
}

export async function fetchMentorById(mentorId: string) {
  try {
    // Safe fields for public access - excluding sensitive data
    const safeFields = 'id, first_name, last_name, profession, location, avatar_url, bio, specialties, status, is_approved';
    
    const { data, error } = await supabase
      .from('profiles')
      .select(safeFields)
      .eq('id', mentorId)
      .eq('profile_type', 'mentor')
      .eq('status', 'Active')
      .eq('is_approved', true)
      .single();
      
    if (error) {
      console.error("Error fetching mentor:", error);
      return { success: false, error, data: null };
    }
    
    if (!data) {
      return { success: false, error: new Error("Mentor not found"), data: null };
    }
    
    // Parse specialties
    let specialtiesArray: string[] = ['Mentoring'];
    if (data.specialties) {
      try {
        if (typeof data.specialties === 'string') {
          const parsed = JSON.parse(data.specialties);
          if (Array.isArray(parsed)) {
            specialtiesArray = parsed;
          } else if (typeof parsed === 'string') {
            specialtiesArray = [parsed];
          }
        } else if (Array.isArray(data.specialties)) {
          specialtiesArray = data.specialties;
        }
      } catch (e) {
        console.error("Error parsing specialties:", e);
      }
    }
    
    // Create full name from first and last name
    const firstName = data.first_name || '';
    const lastName = data.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Anonymous Mentor';
    
    // Fetch reviews from both tables for this specific mentor
    // Fetch appointment reviews (Voice/Video Call reviews)
    const { data: appointmentReviews } = await supabase
      .from('appointment_reviews')
      .select('rating')
      .eq('reviewee_id', mentorId);

    // Fetch subscription reviews (Subscription reviews - mentee_to_mentor only)
    const { data: mentorReviews } = await supabase
      .from('subscription_reviews')
      .select('rating')
      .eq('reviewee_id', mentorId)
      .eq('review_type', 'mentee_to_mentor');

    // Combine all reviews and calculate totals
    const allReviews = [...(appointmentReviews || []), ...(mentorReviews || [])];
    const reviewCount = allReviews.length;
    const averageRating = reviewCount > 0 
      ? allReviews.reduce((sum: number, review: any) => sum + review.rating, 0) / reviewCount
      : undefined;
    
    const mentorData: MentorData = {
      id: data.id,
      name: fullName,
      firstName,
      lastName,
      profession: data.profession || 'Professional Mentor',
      location: data.location || 'Remote',
      avatar: data.avatar_url || '',
      bio: data.bio || 'Professional mentor ready to help you succeed.',
      specialties: specialtiesArray,
      rating: averageRating ? Math.round(averageRating * 10) / 10 : undefined, // Round to 1 decimal
      reviews: reviewCount
    };
    
    return { success: true, data: mentorData };
  } catch (error) {
    console.error("Exception in fetchMentorById:", error);
    return { success: false, error, data: null };
  }
}

export async function fetchUniqueLocations() {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('location')
      .eq('profile_type', 'mentor')
      .eq('status', 'Active')
      .eq('is_approved', true)
      .not('location', 'is', null);
      
    if (error) {
      console.error("Error fetching locations:", error);
      return { success: false, error, data: [] };
    }
    
    if (!data || data.length === 0) {
      return { success: true, data: [] };
    }
    
    // Extract unique locations
    const uniqueLocations = [...new Set(data.map(profile => profile.location))].sort();
    
    return { success: true, data: uniqueLocations };
  } catch (error) {
    console.error("Exception in fetchUniqueLocations:", error);
    return { success: false, error, data: [] };
  }
}
